import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, X, ArrowRight } from "@phosphor-icons/react";

export const StarRating = ({ value = 0, onChange, size = 24, readOnly = false, testidPrefix = "star" }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="inline-flex items-center gap-1" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map(n => {
        const active = n <= (hover || value);
        return (
          <button
            key={n}
            type="button"
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            onClick={() => !readOnly && onChange && onChange(n)}
            disabled={readOnly}
            data-testid={`${testidPrefix}-${n}`}
            className={`transition-transform ${!readOnly ? "hover:scale-110 cursor-pointer" : "cursor-default"}`}
            aria-checked={active}
            role="radio"
          >
            <Star
              size={size}
              weight={active ? "fill" : "regular"}
              className={active ? "text-[#D4AF37]" : "text-zinc-600"}
            />
          </button>
        );
      })}
    </div>
  );
};

export const ReviewModal = ({ booking, onClose, onSubmitted }) => {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/reviews", { booking_id: booking.id, rating, comment: comment.trim() || null });
      toast.success("Review submitted");
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#121214] p-8 relative"
        data-testid="review-modal"
      >
        <button onClick={onClose} className="absolute top-6 right-6 text-zinc-500 hover:text-white" data-testid="review-close-btn">
          <X size={20} />
        </button>
        <div className="eyebrow gold-text mb-2">Rate your experience</div>
        <h2 className="font-serif-display text-3xl text-white">{booking.service_name}</h2>
        <div className="text-sm text-zinc-500 mt-1">{booking.reservation_ref}</div>

        <form onSubmit={submit} className="mt-8 space-y-6">
          <div>
            <label className="eyebrow block mb-3">Your rating</label>
            <StarRating value={rating} onChange={setRating} size={32} />
          </div>
          <div>
            <label className="eyebrow block mb-3">Comments (optional)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="How was your service?"
              data-testid="review-comment-input"
              className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="review-submit-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] text-black font-semibold text-sm disabled:opacity-60"
          >
            {saving ? "Submitting…" : "Submit review"} <ArrowRight size={14} weight="bold" />
          </button>
        </form>
      </div>
    </div>
  );
};
